import {
  buildWorkflowTriggerFields,
  buildDateFieldOptions,
} from './workflowFormHelpers';
import { useEffect, useRef, useState } from 'react';
import {
  getMetadatasPaged,
  getSpecificMetadata,
  IField,
} from '../../services/metadataService';
import {
  createWorkflow,
  editWorkflow,
  executeWorkflow,
  getWorkflow,
} from '../../services/workflowService';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { useLocation } from 'react-router-dom';
import {
  EditItem,
  FilterOperator,
  FilterType,
  serializeFilters,
} from '@moko/ui';
import OtherInfo from '../../common/OtherInfoPortal';
import { Features, useFeatureFlag } from '../../libs/featureAccess';
import { PlayRegular } from '@fluentui/react-icons';

interface TriggerLookupValue {
  key: string;
  value: string;
  header?: boolean;
}

interface TriggerGroup {
  feature: Features;
  header: TriggerLookupValue;
  items: TriggerLookupValue[];
}

const TRIGGER_GROUPS: TriggerGroup[] = [
  {
    feature: Features.FEATURE_ISSUES,
    header: { key: 'issue', value: 'Issue', header: true },
    items: [
      { key: 'OnIssue', value: 'On reporting an issue' },
      { key: 'OnIssueUpdate', value: 'On issue update' },
    ],
  },
  {
    feature: Features.FEATURE_WORK_ORDERS,
    header: { key: 'work', value: 'Work', header: true },
    items: [
      { key: 'OnWork', value: 'On creation of work' },
      { key: 'OnWorkUpdate', value: 'On updating a work' },
      { key: 'OnWorkStatus', value: 'On updating work status' },
      { key: 'OnWorkAssigned', value: 'On assigning a work' },
      { key: 'OnTimesheetEntry', value: 'On creation of timesheet entry' },
    ],
  },
  {
    feature: Features.FEATURE_ORDERS,
    header: { key: 'order', value: 'Order', header: true },
    items: [
      { key: 'OnOrder', value: 'On creation of order' },
      { key: 'OnOrderUpdate', value: 'On updating an order' },
    ],
  },
  {
    feature: Features.FEATURE_ASSETS,
    header: { key: 'asset', value: 'Asset', header: true },
    items: [
      { key: 'OnAsset', value: 'On creation of asset' },
      { key: 'OnAssetUpdate', value: 'On updating an asset' },
    ],
  },
  {
    feature: Features.FEATURE_RENT,
    header: { key: 'rent', value: 'Rent', header: true },
    items: [
      { key: 'OnRentStart', value: 'On starting a rent' },
      { key: 'OnRentEnd', value: 'On ending a rent' },
    ],
  },
];

const Workflow = () => {
  const featureAccess: Record<Features, boolean> = {
    [Features.FEATURE_ISSUES]: useFeatureFlag(Features.FEATURE_ISSUES)
      .isEnabled,
    [Features.FEATURE_WORK_ORDERS]: useFeatureFlag(Features.FEATURE_WORK_ORDERS)
      .isEnabled,
    [Features.FEATURE_ORDERS]: useFeatureFlag(Features.FEATURE_ORDERS)
      .isEnabled,
    [Features.FEATURE_ASSETS]: useFeatureFlag(Features.FEATURE_ASSETS)
      .isEnabled,
    [Features.FEATURE_RENT]: useFeatureFlag(Features.FEATURE_RENT).isEnabled,
  } as Record<Features, boolean>;

  const triggerValues: TriggerLookupValue[] = TRIGGER_GROUPS.filter(
    (group) => featureAccess[group.feature]
  ).flatMap((group) => [group.header, ...group.items]);

  const query = new URLSearchParams(useLocation().search);
  const workflowId = Number.parseInt(query.get('id') ?? '');

  const isSaved = useRef(false);
  const [workflowOtherInfo, setWorkflowOtherInfo] = useState<string>('{}');
  const [triggerType, setTriggerType] = useState<
    'EntityBased' | 'DateFieldBased'
  >('EntityBased');
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [entityTypeDateFields, setEntityTypeDateFields] = useState<IField[]>(
    []
  );

  const dateFieldsRequest = useRef<AbortController | null>(null);
  useEffect(() => () => dateFieldsRequest.current?.abort(), []);

  const fetchEntityTypeData = async (entityType: string) => {
    dateFieldsRequest.current?.abort();
    const abortController = new AbortController();
    dateFieldsRequest.current = abortController;
    setEntityTypeDateFields([]);
    try {
      setIsInProgress(true);
      const fields = new Map<string, IField>();
      const filters = serializeFilters([
        {
          column: { key: 'entity', label: 'Entity', type: FilterType.String },
          operator: FilterOperator.Eq,
          value: entityType,
        },
      ]);
      const pageSize = 100;
      for (let pageNo = 1; ; pageNo += 1) {
        const page = await getMetadatasPaged(
          abortController,
          pageSize,
          pageNo,
          'NAME ASC',
          undefined,
          filters
        );
        if (abortController.signal.aborted) return;
        const items = page.items ?? [];
        const definitions = await Promise.all(
          items.map((item) =>
            getSpecificMetadata(
              abortController,
              item.entity,
              item.name,
              item.version,
              true
            )
          )
        );
        if (abortController.signal.aborted) return;
        for (const definition of definitions) {
          for (const field of definition.fields ?? []) {
            if (field.fieldType === 'Date' || field.fieldType === 'DateTime') {
              // Workflows store a field name, not a metadata-specific field reference.
              if (!fields.has(field.name)) fields.set(field.name, field);
            }
          }
        }
        if (
          page.isLastPage ||
          (page.pageCount != null && pageNo >= page.pageCount) ||
          items.length < pageSize
        )
          break;
      }
      if (entityType === 'TimesheetEntry') {
        fields.set('StartAt', {
          name: 'StartAt',
          fieldType: 'Date',
          label: 'Started At',
        });
        fields.set('EndAt', {
          name: 'EndAt',
          fieldType: 'Date',
          label: 'Ended At',
        });
      }
      setEntityTypeDateFields(
        [...fields.values()].sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (error: any) {
      if (!abortController.signal.aborted) setErrorMessage(error.message);
    } finally {
      if (dateFieldsRequest.current === abortController) setIsInProgress(false);
    }
  };

  const handleRunButtonClick = async () => {
    const abortController = new AbortController();
    try {
      setIsInProgress(true);
      const response = await executeWorkflow(abortController, workflowId);
      setSuccessMessage(
        `Workflow executed on entities with id: ${response.join(', ')}`
      );
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  const dateFieldOptions = buildDateFieldOptions(entityTypeDateFields);

  const isDateFieldBased = triggerType === 'DateFieldBased';

  return (
    <>
      <div>Workflow</div>
      <EditItem
        getAction={async (abortController, id) => {
          const workflow = await getWorkflow(abortController, id);
          const nextTriggerType = workflow.entityType
            ? 'DateFieldBased'
            : 'EntityBased';
          workflow.triggerType = nextTriggerType;
          setTriggerType(nextTriggerType);

          if (workflow.entityType) {
            fetchEntityTypeData(workflow.entityType);
            const raw = workflow.beforeOrAfterDateField ?? '';
            const [diff, unit] = raw.split('|');
            workflow.dateTimeDiff = !diff || diff === 'undefined' ? '' : diff;
            workflow.dateTimeDiff_Unit =
              !unit || unit === 'undefined' ? '' : unit;
          }

          setWorkflowOtherInfo(workflow.otherInfo ?? '{}');
          return workflow;
        }}
        newAction={async (abortController, newWorkflow) => {
          if (
            typeof newWorkflow.name !== 'string' ||
            !newWorkflow.name.trim() ||
            typeof newWorkflow.url !== 'string' ||
            !newWorkflow.url.trim()
          ) {
            throw new Error('Workflow name and URL are required.');
          }
          const newWorkflowId = await createWorkflow(abortController, {
            name: newWorkflow.name,
            url: newWorkflow.url,
            ...buildWorkflowTriggerFields(
              isDateFieldBased,
              newWorkflow,
              entityTypeDateFields
            ),
            otherInfo: workflowOtherInfo,
          });
          setSuccessMessage(`Workflow with id: ${newWorkflowId} created.`);
        }}
        editAction={async (abortController, id, editedWorkflow) => {
          if (
            typeof editedWorkflow.name !== 'string' ||
            !editedWorkflow.name.trim() ||
            typeof editedWorkflow.url !== 'string' ||
            !editedWorkflow.url.trim()
          ) {
            throw new Error('Workflow name and URL are required.');
          }
          await editWorkflow(abortController, id, {
            name: editedWorkflow.name,
            url: editedWorkflow.url,
            ...buildWorkflowTriggerFields(
              isDateFieldBased,
              editedWorkflow,
              entityTypeDateFields
            ),
            otherInfo: workflowOtherInfo,
          });
          setSuccessMessage(`Workflow with id: ${id} successfully updated.`);
        }}
        afterChange={(item) => {
          if ('triggerType' in item) {
            const next =
              item.triggerType === 'DateFieldBased'
                ? 'DateFieldBased'
                : 'EntityBased';
            setTriggerType(next);
          }
          if (typeof item.entityType === 'string' && item.entityType) {
            fetchEntityTypeData(item.entityType);
          }
        }}
        isSaved={isSaved}
        back={'/workflows'}
        postButtons={
          isDateFieldBased
            ? [
                {
                  text: 'Run',
                  onClick: handleRunButtonClick,
                  icon: <PlayRegular />,
                },
              ]
            : undefined
        }
        metadata={{
          fields: [
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
            },
            {
              name: 'url',
              fieldType: 'String',
              label: 'Url',
            },
            {
              name: 'triggerType',
              fieldType: 'Lookup',
              label: 'Trigger Type',
              lookupList: 'TriggerTypes',
            },
            {
              name: 'trigger',
              fieldType: 'Lookup',
              label: 'Trigger',
              lookupList: 'Triggers',
              effect: isDateFieldBased ? 'Hide' : undefined,
            },
            {
              name: 'entityType',
              fieldType: 'Lookup',
              label: 'Entity Type',
              lookupList: 'EntityTypes',
              effect: isDateFieldBased ? undefined : 'Hide',
            },
            {
              name: 'dateField',
              fieldType: 'Lookup',
              label: 'Date Field',
              lookupList: 'DateFields',
              effect: isDateFieldBased ? undefined : 'Hide',
            },
            {
              name: 'dateTimeDiff',
              fieldType: 'NumberWithUnit',
              label: 'Date/Time Difference',
              lookupList: 'DateTimeDiffs',
              effect: isDateFieldBased ? undefined : 'Hide',
            },
          ],
          lookups: [
            {
              name: 'TriggerTypes',
              values: [
                { key: 'EntityBased', value: 'Entity based' },
                { key: 'DateFieldBased', value: 'Date field based' },
              ],
            },
            {
              name: 'EntityTypes',
              values: [
                { key: 'Asset', value: 'Asset' },
                { key: 'Work', value: 'Work' },
                { key: 'TimesheetEntry', value: 'TimesheetEntry' },
              ],
            },
            {
              name: 'DateFields',
              values: dateFieldOptions,
            },
            {
              name: 'DateTimeDiffs',
              values: [
                { key: 'db', value: 'Days before' },
                { key: 'hb', value: 'Hours before' },
                { key: 'mb', value: 'Minutes before' },
                { key: 'da', value: 'Days after' },
                { key: 'ha', value: 'Hours after' },
                { key: 'ma', value: 'Minutes after' },
              ],
            },
            {
              name: 'Triggers',
              values: triggerValues,
            },
          ],
          validations: [],
          interactions: [],
        }}
      >
        <OtherInfo
          metadata={{
            fields: [
              {
                name: 'authentication',
                fieldType: 'Lookup',
                label: 'Authentication method',
                lookupList: 'AuthenticationMethods',
              },
              {
                name: 'userName',
                fieldType: 'String',
                label: 'Username',
              },
              {
                name: 'password',
                fieldType: 'Password',
                label: 'Password',
              },
            ],
            lookups: [
              {
                name: 'AuthenticationMethods',
                values: [{ key: 'Basic', value: 'Basic Authentication' }],
              },
            ],
            validations: [],
            interactions: [],
          }}
          otherInfo={workflowOtherInfo ?? '{}'}
          isSaved={isSaved}
          onOtherInfoChanged={(newValue: string) => {
            setWorkflowOtherInfo(JSON.stringify(newValue));
          }}
          hideTitle
        />
      </EditItem>
    </>
  );
};

export default Workflow;
